import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { adminStatisticsApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Loading from '@/components/ui/Loading'
import Badge from '@/components/ui/Badge'
import {
  Users,
  Package,
  DollarSign,
  TrendingUp,
  MessageSquare,
  Star,
  Database,
  Activity,
  FileText,
  AlertCircle,
  CheckCircle,
  BarChart3,
} from 'lucide-react'
import { formatNumber } from '@/utils/format'

export default function Statistics() {
  const { user } = useAuth()

  // Only Super Admins can access this page
  if (user?.user_type !== 'super_admin') {
    return <Navigate to="/dashboard" />
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-statistics'],
    queryFn: () => adminStatisticsApi.get(),
    refetchInterval: 60000, // Refetch every minute
  })

  if (isLoading) return <Loading />

  const stats = data?.data

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">Failed to load statistics</h2>
            <p className="text-gray-600 mt-2">Please try again later</p>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-primary-600" />
            <span>System Statistics</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Comprehensive overview of platform metrics
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Last updated</p>
          <p className="text-sm font-semibold text-gray-900">{new Date(stats.generated_at).toLocaleString()}</p>
        </div>
      </div>

      {/* User Statistics */}
      <Card title="User Statistics">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.users.total_users)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">New (7 days)</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.users.new_users_last_7_days)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">New (30 days)</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.users.new_users_last_30_days)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">By Type</p>
            {stats.users.users_by_type.map((item: any) => (
              <div key={item.user_type} className="flex justify-between items-center">
                <Badge variant={item.user_type === 'professional' ? 'success' : 'primary'}>
                  {item.user_type}
                </Badge>
                <span className="text-sm font-semibold text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {stats.users.users_by_continent.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-semibold text-gray-700 mb-3">Top Continents</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stats.users.users_by_continent.slice(0, 5).map((item: any) => (
                <div key={item.continent} className="text-center">
                  <p className="text-lg font-bold text-gray-900">{item.count}</p>
                  <p className="text-xs text-gray-600">{item.continent}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Subscription Statistics */}
      <Card title="Subscription Statistics">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.subscriptions.total_active_subscriptions)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.subscriptions.total_cancelled_subscriptions)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">By Status</p>
            {stats.subscriptions.subscriptions_by_status.map((item: any) => (
              <div key={item.subscription_status} className="flex justify-between items-center">
                <Badge status={item.subscription_status}>
                  {item.subscription_status || 'none'}
                </Badge>
                <span className="text-sm font-semibold text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">By Tier</p>
            {stats.subscriptions.subscription_tier_distribution.map((item: any) => (
              <div key={item.subscription_tier} className="flex justify-between items-center">
                <Badge variant={item.subscription_tier === 'paid' ? 'success' : 'primary'}>
                  {item.subscription_tier || 'free'}
                </Badge>
                <span className="text-sm font-semibold text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Barter & Financial Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Barter Statistics">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Barters</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.barters.total_barters)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">New (7 days)</p>
                <p className="text-lg font-bold text-gray-900">{formatNumber(stats.barters.barters_created_last_7_days)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">New (30 days)</p>
                <p className="text-lg font-bold text-gray-900">{formatNumber(stats.barters.barters_created_last_30_days)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Value</p>
                <p className="text-lg font-bold text-green-600">${formatNumber(stats.barters.total_barter_value)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Average Value</p>
                <p className="text-lg font-bold text-green-600">${Number(stats.barters.avg_barter_value).toFixed(2)}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">By Status</p>
              <div className="space-y-2">
                {stats.barters.barters_by_status.map((item: any) => (
                  <div key={item.status} className="flex justify-between items-center">
                    <Badge status={item.status}>{item.status}</Badge>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">By Type</p>
              <div className="space-y-2">
                {stats.barters.barters_by_type.map((item: any) => (
                  <div key={item.type} className="flex justify-between items-center">
                    <Badge variant={item.type === 'goods' ? 'primary' : 'success'}>{item.type}</Badge>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Financial Statistics">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Barter Coins</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.financial.total_barter_coins_issued)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Transactions</p>
                <p className="text-lg font-bold text-gray-900">{formatNumber(stats.financial.total_transactions)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Transaction Value</p>
                <p className="text-lg font-bold text-green-600">{formatNumber(stats.financial.total_transaction_value)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Withdrawals</p>
                <p className="text-lg font-bold text-gray-900">{formatNumber(stats.financial.total_withdrawals)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Withdrawal Amount</p>
                <p className="text-lg font-bold text-red-600">{formatNumber(stats.financial.total_withdrawal_amount)}</p>
              </div>
            </div>

            <div className="pt-4 border-t bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-yellow-900">Pending Withdrawals</p>
                <Badge variant="warning">{formatNumber(stats.financial.pending_withdrawals)}</Badge>
              </div>
              <p className="text-2xl font-bold text-yellow-700">
                {formatNumber(stats.financial.pending_withdrawal_amount)} coins
              </p>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">Transaction Types</p>
              <div className="space-y-2">
                {stats.financial.transactions_by_type.map((item: any) => (
                  <div key={item.transaction_type} className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">{item.transaction_type}</span>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Barter Groups & Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Barter Group Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Groups</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.groups.total_groups)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Completed Groups</p>
                <p className="text-lg font-bold text-green-600">{formatNumber(stats.groups.completed_groups)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Avg Barters/Group</p>
                <p className="text-lg font-bold text-gray-900">{Number(stats.groups.avg_barters_per_group).toFixed(1)}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">By Status</p>
              <div className="space-y-2">
                {stats.groups.groups_by_status.map((item: any) => (
                  <div key={item.status} className="flex justify-between items-center">
                    <Badge status={item.status}>{item.status}</Badge>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Barter Request Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.requests.total_requests)}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">New (7 days)</p>
              <p className="text-lg font-bold text-gray-900">{formatNumber(stats.requests.requests_last_7_days)}</p>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">By Status</p>
              <div className="space-y-2">
                {stats.requests.requests_by_status.map((item: any) => (
                  <div key={item.status} className="flex justify-between items-center">
                    <Badge status={item.status}>{item.status}</Badge>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Reviews, Support & Messages */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Review Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Reviews</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.reviews.total_reviews)}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Average Rating</p>
              <p className="text-2xl font-bold text-yellow-600">{Number(stats.reviews.average_rating).toFixed(2)} ★</p>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">Rating Distribution</p>
              <div className="space-y-2">
                {stats.reviews.rating_distribution.map((item: any) => (
                  <div key={item.rating} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{item.rating} ★</span>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Support Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.support.total_tickets)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Open Tickets</p>
                <p className="text-lg font-bold text-red-600">{formatNumber(stats.support.open_tickets)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Avg Response Time</p>
                <p className="text-lg font-bold text-gray-900">{Number(stats.support.avg_response_time_hours).toFixed(1)}h</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">By Status</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {stats.support.tickets_by_status.map((item: any) => (
                  <div key={item.status} className="flex justify-between items-center">
                    <Badge status={item.status}>{item.status}</Badge>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Message Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(
                    stats.messages.total_channel_messages +
                    stats.messages.total_direct_messages +
                    stats.messages.total_group_messages
                  )}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Last 24 Hours</p>
              <p className="text-lg font-bold text-blue-600">{formatNumber(stats.messages.messages_last_24_hours)}</p>
            </div>

            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Channel</span>
                <span className="text-sm font-semibold text-gray-900">{formatNumber(stats.messages.total_channel_messages)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Direct</span>
                <span className="text-sm font-semibold text-gray-900">{formatNumber(stats.messages.total_direct_messages)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Group</span>
                <span className="text-sm font-semibold text-gray-900">{formatNumber(stats.messages.total_group_messages)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Database & System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Database Statistics">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Database Size</p>
                <p className="text-2xl font-bold text-gray-900">{stats.database.database_size_mb} MB</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">Top Tables by Size</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.database.table_stats.map((table: any) => (
                  <div key={table.table_name} className="flex justify-between items-center">
                    <span className="text-xs text-gray-700 font-mono">{table.table_name}</span>
                    <div className="text-right">
                      <span className="text-xs font-semibold text-gray-900">{table.size_mb} MB</span>
                      <span className="text-xs text-gray-500 ml-2">({formatNumber(table.table_rows)} rows)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="System Health">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">System Status</p>
                <p className="text-xl font-bold text-green-600">
                  {stats.system_health.maintenance_mode ? 'Maintenance Mode' : 'Operational'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Images</p>
                <p className="text-lg font-bold text-gray-900">{formatNumber(stats.system_health.total_images)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Failed Transactions</p>
                <p className="text-lg font-bold text-red-600">{formatNumber(stats.system_health.failed_transactions)}</p>
              </div>
            </div>

            {stats.system_health.maintenance_mode === 1 && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-900">Maintenance Mode Active</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      The system is currently in maintenance mode
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

    </div>
  )
}
